import React, { useContext } from 'react';
import { MdContentCopy } from 'react-icons/md';

import { LocaleContext } from '../../contexts/LocaleContext';
import { SiteSettingsContext } from '../../contexts/SiteSettingsContext';

import useHandleCopyWithToast from '../../utils/handleCopyWithToast';
import { useUiComponentsInternationalContext } from '@repo/static-translations';

import { Input } from '../shadcn/Input';
import { Button } from '../shadcn/Button';
import { GetInternalLinkFunctionType } from '../../models/GetInternalLinkFunctionType';
import { SanityLink } from '../../models/gatsby-graphql-types';

interface ReferralLinkProps {
  referralCode: string;
  getInternalLink: GetInternalLinkFunctionType;
}

const isBrowser = typeof window !== 'undefined';

const getShareUrl = (
  referralProgramLink: SanityLink,
  currency: string,
  referralCode: string,
  getInternalLink: GetInternalLinkFunctionType
) => {
  let url = '';

  if (isBrowser && referralProgramLink?.linkType === 'internal') {
    url = `${window.location.origin}${getInternalLink({
      reference: referralProgramLink.internalLink,
      currency,
    })}`;
  }

  if (referralProgramLink?.linkType === 'external') {
    url = referralProgramLink.href;
  }

  if (isBrowser && url) {
    try {
      const shareUrl = new URL(url);
      shareUrl.searchParams.append('referral_code', referralCode);

      return shareUrl.href;
    } catch (err) {
      console.error(err);
      return '';
    }
  }

  return '';
};

const ReferralLink = ({ referralCode, getInternalLink }: ReferralLinkProps) => {
  const { currency } = useContext(LocaleContext);
  const { siteSettings } = useContext(SiteSettingsContext);

  const { tt } = useUiComponentsInternationalContext();
  const handleCopyWithToast = useHandleCopyWithToast();

  const shareUrl = getShareUrl(
    siteSettings.referralProgramLink,
    currency,
    referralCode,
    getInternalLink
  );

  const handleSelectLink = event => {
    event.preventDefault();
    event.target.select();
  };

  return (
    <div className="flex self-stretch mb-2">
      <Input
        type="url"
        className="rounded-r-none"
        value={shareUrl}
        onClick={handleSelectLink}
      />
      <Button
        className="rounded-l-none"
        onClick={() =>
          handleCopyWithToast(
            shareUrl,
            tt(s => s.referralProgramComponent.referralLinkLabel)
          )
        }
      >
        <MdContentCopy size={24} aria-hidden="true" />
        {tt(s => s.referralProgramComponent.copyLinkButton)}
      </Button>
    </div>
  );
};

export default ReferralLink;
